import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plane, Search, ChevronLeft, ChevronRight, Filter, Calendar, Clock, ClipboardList, Car, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { flightApi } from '@/services/api';
import { Flight, FlightSegment } from '@/types';
import { format, parseISO, isSameDay } from 'date-fns';
import { zhCN } from 'date-fns/locale';

const STATUS_OPTIONS = [
  { value: 'all', label: '全部' },
  { value: 'scheduled', label: '计划中' },
  { value: 'in_progress', label: '执行中' },
  { value: 'completed', label: '已完成' },
] as const;

const PAGE_SIZE = 10;

const getStatusMeta = (status: string) => {
  switch (status) {
    case 'scheduled':
      return {
        border: 'border-l-gray-400',
        badgeBg: 'bg-gray-50',
        badgeText: 'text-gray-600',
        badgeBorder: 'border-gray-200',
      };
    case 'in_progress':
      return {
        border: 'border-l-blue-500',
        badgeBg: 'bg-blue-50',
        badgeText: 'text-blue-600',
        badgeBorder: 'border-blue-200',
      };
    case 'completed':
      return {
        border: 'border-l-green-500',
        badgeBg: 'bg-green-50',
        badgeText: 'text-green-600',
        badgeBorder: 'border-green-200',
      };
    default:
      return {
        border: 'border-l-gray-400',
        badgeBg: 'bg-gray-50',
        badgeText: 'text-gray-600',
        badgeBorder: 'border-gray-200',
      };
  }
};

const getStatusText = (status: string) => {
  switch (status) {
    case 'scheduled': return '计划中';
    case 'in_progress': return '执行中';
    case 'completed': return '已完成';
    default: return status;
  }
};

const formatTime = (timeStr: string | undefined) => {
  if (!timeStr) return '--:--';
  try {
    return format(parseISO(timeStr), 'HH:mm');
  } catch {
    return '--:--';
  }
};

const formatDurationShort = (minutes: number) => {
  if (!minutes || minutes <= 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}h${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
};


const formatDateShort = (timeStr: string | undefined) => {
  if (!timeStr) return '';
  try {
    return format(parseISO(timeStr), 'MM-dd');
  } catch {
    return '';
  }
};

const isDateToday = (timeStr: string | undefined) => {
  if (!timeStr) return false;
  try {
    return isSameDay(parseISO(timeStr), new Date());
  } catch {
    return false;
  }
};

const isNextDay = (depTime: string | undefined, arrTime: string | undefined) => {
  if (!depTime || !arrTime) return false;
  try {
    return !isSameDay(parseISO(depTime), parseISO(arrTime));
  } catch {
    return false;
  }
};

const calcSegmentDuration = (dep: string | undefined, arr: string | undefined) => {
  if (!dep || !arr) return 0;
  try {
    return Math.round((parseISO(arr).getTime() - parseISO(dep).getTime()) / 60000);
  } catch {
    return 0;
  }
};

const getSegments = (flight: Flight): FlightSegment[] => {
  if (flight.segments != null && flight.segments.length > 0) return flight.segments;
  if (flight.legs != null && flight.legs.length > 0) return flight.legs;
  return [];
};

interface DateGroup {
  date: string;
  day: string;
  weekday: string;
  isToday: boolean;
  flights: Flight[];
}

const groupByDate = (flights: Flight[]): DateGroup[] => {
  const groups: DateGroup[] = [];
  flights.forEach((flight) => {
    const dateStr = flight.startTime ? flight.startTime.split('T')[0] : '';
    if (!dateStr) return;
    const existing = groups.find(g => g.date === dateStr);
    if (existing) {
      existing.flights.push(flight);
    } else {
      const d = parseISO(flight.startTime);
      groups.push({
        date: dateStr,
        day: format(d, 'MM-dd'),
        weekday: format(d, 'EEE', { locale: zhCN }),
        isToday: isDateToday(flight.startTime),
        flights: [flight],
      });
    }
  });
  return groups;
};

/** 单个航段信息展示 - 响应式 */
function FlightSegmentInfo({ segment }: { segment: FlightSegment }) {
  const segDuration = calcSegmentDuration(segment.planDepTime, segment.planArrTime);
  const nextDay = isNextDay(segment.planDepTime, segment.planArrTime);

  return (
    <div className="space-y-1 min-w-0 max-w-md">
      {/* 航班号 */}
      <div className="text-sm font-bold text-blue-600 lg:text-xs lg:font-medium lg:text-gray-700">
        {segment.flightNo}
      </div>

      {/* 机场名称 */}
      <div className="flex justify-between text-sm text-gray-600 lg:text-xs lg:text-gray-500">
        <span className="truncate mr-1">{segment.depCode} {segment.depName}</span>
        <span className="truncate ml-1 text-right">{segment.arrCode} {segment.arrName}</span>
      </div>

      {/* 时间行：出发 - duration line - 到达 */}
      <div className="flex items-start gap-2 mt-2 lg:mt-1">
        {/* 出发时间 */}
        <div className="text-center min-w-[46px] lg:min-w-[52px]">
          <div className="text-xl font-bold text-gray-900 lg:text-base lg:font-semibold">
            {formatTime(segment.planDepTime)}
          </div>
          <div className="text-xs text-gray-400 lg:text-[10px]">
            {formatDateShort(segment.planDepTime)}
          </div>
        </div>

        {/* Duration line + Plane */}
        <div className="flex-1 flex flex-col items-center min-w-0 pt-2 lg:pt-1.5">
          <div className="w-full h-px bg-gray-200 relative">
            <Plane className="w-4 h-4 text-gray-400 lg:w-3.5 lg:h-3.5 absolute left-1/2 -top-2 lg:-top-[7px] -translate-x-1/2" />
          </div>
          <div className="text-xs text-gray-400 lg:text-[10px] mt-1.5 lg:mt-1">
            {formatDurationShort(segDuration)}
          </div>
        </div>

        {/* 到达时间 */}
        <div className="text-center min-w-[46px] lg:min-w-[52px]">
          <div className="text-xl font-bold text-gray-900 lg:text-base lg:font-semibold">
            {formatTime(segment.planArrTime)}
            {nextDay && (
              <span className="text-sm text-red-500 lg:text-xs lg:text-gray-400 ml-0.5 align-super">+1</span>
            )}
          </div>
          <div className="text-xs text-gray-400 lg:text-[10px]">
            {formatDateShort(segment.planArrTime)}
          </div>
        </div>
      </div>
    </div>
  );
}

/** 航班右侧信息（总时长/签到/出车） */
function FlightMetaInfo({ flight, compact }: { flight: Flight; compact?: boolean }) {
  return (
    <div className={`space-y-2 text-sm text-gray-600 lg:text-xs lg:text-gray-500 ${compact ? 'space-y-1' : ''}`}>
      {flight.durationMinutes > 0 && (
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-400 lg:w-3.5 lg:h-3.5" />
          <span>总时长 {formatDurationShort(flight.durationMinutes)}</span>
        </div>
      )}
      {flight.signTime && (
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-gray-400 lg:w-3.5 lg:h-3.5" />
          <span>签到 {formatTime(flight.signTime)}</span>
        </div>
      )}
      {flight.driveTime && (
        <div className="flex items-center gap-2">
          <Car className="w-4 h-4 text-gray-400 lg:w-3.5 lg:h-3.5" />
          <span>出车 {formatTime(flight.driveTime)}</span>
        </div>
      )}
    </div>
  );
}

export function FlightListPage() {
  const navigate = useNavigate();
  const [flights, setFlights] = useState<Flight[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const isFetching = useRef(false);

  const fetchFlights = useCallback(async () => {
    if (isFetching.current) return;
    isFetching.current = true;
    setLoading(true);
    try {
      const response = await flightApi.getFlights({
        page: currentPage,
        pageSize: PAGE_SIZE,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      });
      const data = response.data;

      if (data && data.results && Array.isArray(data.results)) {
        setFlights(data.results);
        setTotalCount(data.count || 0);
        const pages = Math.ceil((data.count || 0) / PAGE_SIZE);
        setTotalPages(pages);
      } else {
        const fallbackData = Array.isArray(data) ? data : data?.data || [];
        setFlights(fallbackData);
        setTotalCount(fallbackData.length);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Failed to fetch flights:', error);
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, [currentPage, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  useEffect(() => {
    fetchFlights();
  }, [fetchFlights]);

  // 清除展开状态（切换筛选/翻页时重置）
  useEffect(() => {
    setExpandedIds(new Set());
  }, [currentPage, statusFilter]);

  const toggleExpand = useCallback((flightId: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(flightId)) {
        next.delete(flightId);
      } else {
        next.add(flightId);
      }
      return next;
    });
  }, []);

  const filteredFlights = flights.filter((flight) => {
    if (!searchTerm) return true;
    const flightNumbers = flight.flightNumbers ?? [];
    const route = flight.route ?? '';
    const departure = flight.departure ?? '';
    const arrival = flight.arrival ?? '';
    const searchLower = searchTerm.toLowerCase();
    return (
      flightNumbers.some((fn) => fn.toLowerCase().includes(searchLower)) ||
      route.toLowerCase().includes(searchLower) ||
      departure.toLowerCase().includes(searchLower) ||
      arrival.toLowerCase().includes(searchLower)
    );
  });

  const dateGroups = useMemo(() => groupByDate(filteredFlights), [filteredFlights]);

  // 预计算扁平航班列表用于时间轴首/尾判断（避免渲染中的副作用）
  const flatFlightIds = useMemo(
    () => dateGroups.flatMap(g => g.flights.map(f => f.id)),
    [dateGroups]
  );
  const firstFlightId = flatFlightIds[0];
  const lastFlightId = flatFlightIds[flatFlightIds.length - 1];

  if (loading) {
    return (
      <div className="container mx-auto py-6 max-w-5xl">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto py-4 lg:py-6 max-w-5xl px-3 sm:px-4 lg:px-6">
      {/* 页面标题 - 移动端居中，桌面端左对齐 */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-2">
        <div className="text-center lg:text-left">
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
            航班列表
            <span className="text-gray-400 text-base lg:text-lg font-normal ml-2">/ Duty Timeline</span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            共 {totalCount} 个航班
            {searchTerm && `（搜索 "${searchTerm}" 的结果）`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 lg:flex-initial">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="搜索航班号、航线、机场..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full lg:w-64 h-9"
            />
          </div>
          <Button variant="outline" size="sm" className="hidden lg:flex h-9 px-3" onClick={() => navigate('/flights/calendar')}>
            <Calendar className="w-4 h-4 mr-1" />
            日历
          </Button>
          <Button variant="outline" size="sm" className="h-9 px-3">
            <Filter className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">筛选</span>
          </Button>
        </div>
      </div>

      {/* 状态筛选 - 移动端 Tab 下划线样式，桌面端 pill 样式 */}
      <div className="mb-6">
        {/* 移动端 Tab 样式 */}
        <div className="flex lg:hidden border-b border-gray-200">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors relative ${
                statusFilter === opt.value
                  ? 'text-blue-600'
                  : 'text-gray-500'
              }`}
              onClick={() => setStatusFilter(opt.value)}
            >
              {opt.label}
              {statusFilter === opt.value && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
              )}
            </button>
          ))}
        </div>
        {/* 桌面端 pill 样式 */}
        <div className="hidden lg:flex flex-wrap gap-2">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`h-8 px-4 text-xs font-medium rounded-full transition-colors ${
                statusFilter === opt.value
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
              onClick={() => setStatusFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* 航班时间轴 */}
      {filteredFlights.length === 0 ? (
        <div className="text-center py-20">
          <Plane className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 text-lg">暂无航班数据</p>
          <p className="text-sm text-gray-400 mt-1">
            {searchTerm || statusFilter !== 'all'
              ? '没有找到匹配的航班，请尝试调整筛选条件'
              : '请先同步您的航班日程'}
          </p>
        </div>
      ) : (
        <div className="space-y-0">
          {dateGroups.map((group) => (
            <div key={group.date}>
              {group.flights.map((flight, fIdx) => {
                const status = flight.status ?? 'scheduled';
                const statusMeta = getStatusMeta(status);
                const isExpanded = expandedIds.has(flight.id);
                const segments = getSegments(flight);
                const isFirstInGroup = fIdx === 0;
                const isFirstOverall = flight.id === firstFlightId;
                const isLastOverall = flight.id === lastFlightId;
                const displayRoute = flight.formattedRoute || flight.route || '-';
                const legCount = segments.length;

                return (
                  <div key={flight.id} className="flex gap-2 lg:gap-5 mb-3 lg:mb-4">
                    {/* 时间轴列：日期 + 星期 + 圆点 + 连接线 */}
                    <div className="w-12 lg:w-14 shrink-0 flex flex-col items-center pt-1 lg:pt-3">
                      {isFirstInGroup && (
                        <>
                          {/* 移动端：日期 + 星期分行 */}
                          <div className={`lg:hidden text-lg font-bold whitespace-nowrap ${group.isToday ? 'text-blue-500' : 'text-gray-600'}`}>
                            {group.day}
                          </div>
                          <div className="lg:hidden text-xs text-gray-400 mb-1">{group.weekday}</div>
                          {/* 桌面端：日期 + 星期同行 */}
                          <div className={`hidden lg:block rounded-lg px-2 py-1.5 text-center min-w-[52px] whitespace-nowrap ${
                            group.isToday ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
                          }`}>
                            <span className="text-sm font-bold">{group.day}</span>
                            <span className={`text-xs ml-1 ${group.isToday ? 'text-blue-100' : 'text-gray-400'}`}>{group.weekday}</span>
                          </div>
                        </>
                      )}
                      {/* 圆点 */}
                      <div className="lg:my-2">
                        <div className={`rounded-full ${
                          isFirstOverall
                            ? 'w-3 h-3 lg:w-4 lg:h-4 bg-blue-500'
                            : isLastOverall
                              ? 'w-3 h-3 lg:w-4 lg:h-4 border-2 border-gray-300 bg-white'
                              : 'w-3 h-3 lg:w-4 lg:h-4 bg-gray-300'
                        }`} />
                      </div>
                      {/* 连接线 */}
                      {!isLastOverall && (
                        <div className="w-px flex-1 bg-gray-200 my-1 lg:my-0" />
                      )}
                    </div>

                    {/* 航班卡片 */}
                    <div className={`flex-1 bg-white rounded-xl lg:rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden ${statusMeta.border} border-l-4`}>
                      {/* ──────── 移动端布局 ──────── */}
                      <div className="lg:hidden">
                        {/* 点击标题行展开/折叠 */}
                        <div className="p-3 cursor-pointer" onClick={() => toggleExpand(flight.id)}>
                          <div className="flex items-center justify-between mb-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${statusMeta.badgeBg} ${statusMeta.badgeText} ${statusMeta.badgeBorder}`}>
                              {getStatusText(status)}
                            </span>
                            <div className="flex items-center gap-2">
                              {legCount > 0 && !isExpanded && (
                                <span className="text-xs text-gray-400">{legCount} 航段</span>
                              )}
                              {isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-blue-500" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-blue-500" />
                              )}
                            </div>
                          </div>

                          <h3 className="text-xl font-bold text-gray-900 mb-1">{flight.flightNumbers?.join(' / ') || '-'}</h3>
                          <p className="text-sm text-gray-500">{displayRoute}</p>
                          {!isExpanded && (
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                              {flight.durationMinutes > 0 && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3.5 h-3.5 text-gray-400" />
                                  {formatDurationShort(flight.durationMinutes)}
                                </span>
                              )}
                              {flight.signTime && (
                                <span className="text-xs text-gray-500">签到 {formatTime(flight.signTime)}</span>
                              )}
                            </div>
                          )}
                          {!isExpanded && (flight.acTypeCode || flight.acNo) && (
                            <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-400">
                              <Plane className="w-3.5 h-3.5" />
                              {flight.acTypeCode && <span>{flight.acTypeCode}</span>}
                              {flight.acNo && <><span className="text-gray-300 mx-0.5">|</span><span>{flight.acNo}</span></>}
                              {flight.postName && <><span className="text-gray-300 mx-0.5">|</span><span>{flight.postName}</span></>}
                            </div>
                          )}
                        </div>

                        {/* 展开内容 */}
                        {isExpanded && (
                          <div className="px-3 pb-3 border-t border-gray-100">
                            <div className="pt-3">
                              {segments.map((seg, idx) => (
                                <div key={idx} className={`${idx > 0 ? 'mt-4 pt-4 border-t border-gray-50' : ''}`}>
                                  <FlightSegmentInfo segment={seg} />
                                </div>
                              ))}
                              {segments.length === 0 && (
                                <div className="text-xs text-gray-400 py-2">暂无航段信息</div>
                              )}
                            </div>
                            {legCount > 0 && (
                              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100 text-sm text-gray-500">
                                <Plane className="w-4 h-4 text-gray-400" />
                                <span>{segments[0].acTypeCode}</span>
                                {segments[0].acNo && <><span className="text-gray-300">|</span><span>{segments[0].acNo}</span></>}
                                {segments[0].postName && <><span className="text-gray-300">|</span><span>{segments[0].postName}</span></>}
                              </div>
                            )}
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <FlightMetaInfo flight={flight} />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* ──────── 桌面端布局 ──────── */}
                      <div className="hidden lg:block">
                        {/* 标题行（可点击展开/折叠） */}
                        <div
                          className="flex items-center p-4 cursor-pointer select-none"
                          onClick={() => toggleExpand(flight.id)}
                        >
                          {/* 状态标签 */}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium mr-4 ${statusMeta.badgeBg} ${statusMeta.badgeText} ${statusMeta.badgeBorder}`}>
                            {getStatusText(status)}
                          </span>

                          {/* 航班号 + 航线 */}
                          <div className="flex-1 min-w-0 mr-4">
                            <h3 className="text-base font-bold text-gray-900 truncate">
                              {flight.flightNumbers?.join(' / ') || '-'}
                            </h3>
                            <p className="text-sm text-gray-500 truncate">{displayRoute}</p>
                          </div>

                          {/* 航段数量标记 */}
                          {legCount > 0 && !isExpanded && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs text-gray-400 bg-gray-50 border border-gray-100 mr-3">
                              {legCount} 航段
                            </span>
                          )}

                          {/* 摘要信息（折叠时显示） */}
                          {!isExpanded && (
                            <div className="flex items-center gap-4 mr-4 text-xs text-gray-500">
                              {flight.durationMinutes > 0 && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3.5 h-3.5 text-gray-400" />
                                  {formatDurationShort(flight.durationMinutes)}
                                </span>
                              )}
                              {flight.signTime && (
                                <span>签到 {formatTime(flight.signTime)}</span>
                              )}
                              {flight.driveTime && (
                                <span>出车 {formatTime(flight.driveTime)}</span>
                              )}
                            </div>
                          )}

                          {/* 机型信息（折叠时显示） */}
                          {!isExpanded && (flight.acTypeCode || flight.acNo) && (
                            <div className="flex items-center gap-1.5 text-xs text-gray-400 mr-4">
                              <Plane className="w-3.5 h-3.5" />
                              {flight.acTypeCode && <span>{flight.acTypeCode}</span>}
                              {flight.acNo && <span>{flight.acNo}</span>}
                              {flight.postName && <><span className="text-gray-300 mx-0.5">|</span><span>{flight.postName}</span></>}
                            </div>
                          )}

                          {/* 展开/折叠箭头 */}
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-blue-500 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          )}
                        </div>

                        {/* 展开内容：纵向堆叠航段 */}
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t border-gray-100 ml-7 lg:ml-8">
                            <div className="pt-4">
                                  {segments.map((seg, idx) => (
                                    <div key={idx} className={`${idx > 0 ? 'mt-4 pt-4 border-t border-gray-100' : ''}`}>
                                      <div className="flex items-start gap-4">
                                        {/* 航段序号 */}
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-bold">
                                          {idx + 1}
                                        </div>
                                        {/* 航段详情 */}
                                        <div className="flex-1 min-w-0">
                                          <FlightSegmentInfo segment={seg} />
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                  {segments.length === 0 && (
                                    <div className="text-sm text-gray-400 py-4">暂无航段信息</div>
                                  )}
                                </div>

                                {/* 底部信息行 */}
                                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                                  {/* 机型 / 机号 / 岗位 */}
                                  <div className="flex items-center gap-3 text-sm text-gray-500 min-w-0">
                                    {legCount > 0 && segments[0].acTypeCode && (
                                      <>
                                        <span className="flex items-center gap-1.5">
                                          <Plane className="w-4 h-4 text-gray-400" />
                                          {segments[0].acTypeCode}
                                        </span>
                                        {segments[0].acNo && <span className="text-gray-300">|</span>}
                                        {segments[0].acNo && <span>{segments[0].acNo}</span>}
                                        {segments[0].postName && <span className="text-gray-300">|</span>}
                                        {segments[0].postName && <span>{segments[0].postName}</span>}
                                      </>
                                    )}
                                  </div>
                                  {/* 总时长 / 签到 / 出车 */}
                                  <FlightMetaInfo flight={flight} compact />
                                </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* 底部分页/统计 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-4 border-t border-gray-200 gap-3 sm:gap-0 mt-4">
        <p className="text-xs text-gray-500 text-center sm:text-left">
          显示 {filteredFlights.length} 个航班
          {totalPages > 1 && `（第 ${currentPage} / ${totalPages} 页）`}
        </p>
        {totalPages > 1 && (
          <div className="flex items-center justify-center sm:justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">上一页</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
            >
              <span className="hidden sm:inline mr-1">下一页</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
